/**
 * Target selection (spec §6 "Targeting (T / Y)"): the living enemy nearest the reticle within the targeting
 * cone, else the nearest living enemy; repeated presses cycle by angle; a dead or despawned target is replaced
 * by the next selection. Relative vectors are float64 (D-011); every function is allocation-free (D-014).
 */
import { Hull, Life, Pos64, Rot, Target, Team } from '../world/components'
import { isHostile } from './hostility'

/** Live ship entity ids (pass `sim.ships.dense` after `commitRemovals`). */
export type ShipList = ArrayLike<number>

/**
 * Targeting tuning. Mutable so the dev panel can bind it. `cone` is radians (spec §6: 30°).
 * TODO(G13): move into weapons.json (`targetConeDeg`) when schemas.ts is next owned (G3 owns no data file).
 */
export const TARGETING = { cone: (30 * Math.PI) / 180 }

/** Caller-owned selection result. `cosAngle` is the cosine of the angle off the nose; `distance` is metres. */
export interface TargetPick {
  eid: number
  cosAngle: number
  distance: number
}

export function createTargetPick(): TargetPick {
  return { eid: -1, cosAngle: -1, distance: 0 }
}

/** True when `t` is a living ship of a team hostile to `shooter`'s (`isHostile`, M4 I1) (not the shooter itself). Membership in the live list is not checked. */
export function isLivingEnemy(shooter: number, t: number): boolean {
  return t >= 0 && t !== shooter && Life.alive[t] === 1 && Hull.value[t] > 0 && isHostile(Team.id[shooter], Team.id[t])
}

function contains(ships: ShipList, eid: number): boolean {
  for (let i = 0; i < ships.length; i++) if (ships[i] === eid) return true
  return false
}

// Scratch: the shooter's nose (world unit vector) and the last measured target geometry.
let noseX = 0
let noseY = 0
let noseZ = -1
let measuredCos = 0
let measuredDist = 0

/** The ship's nose (local −Z) in world space, written to the module scratch. */
function loadNose(eid: number): void {
  const qx = Rot.x[eid]
  const qy = Rot.y[eid]
  const qz = Rot.z[eid]
  const qw = Rot.w[eid]
  // rotate (0, 0, −1) by q
  noseX = -2 * (qx * qz + qw * qy)
  noseY = -2 * (qy * qz - qw * qx)
  noseZ = -(1 - 2 * (qx * qx + qy * qy))
}

/** Measures `t` from `shooter` (nose must be loaded): writes `measuredCos` and `measuredDist`. */
function measure(shooter: number, t: number): void {
  const rx = Pos64.x[t] - Pos64.x[shooter]
  const ry = Pos64.y[t] - Pos64.y[shooter]
  const rz = Pos64.z[t] - Pos64.z[shooter]
  const d = Math.sqrt(rx * rx + ry * ry + rz * rz)
  measuredDist = d
  measuredCos = d > 0 ? (rx * noseX + ry * noseY + rz * noseZ) / d : 1
}

function setPick(out: TargetPick, eid: number, cosAngle: number, distance: number): number {
  out.eid = eid
  out.cosAngle = cosAngle
  out.distance = distance
  return eid
}

/**
 * The living enemy nearest the reticle (nose) within `TARGETING.cone`, else the nearest living enemy, else -1.
 * Pure: fills `out` and returns the eid; it does not write `Target`.
 */
export function selectTarget(ships: ShipList, shooter: number, out: TargetPick): number {
  loadNose(shooter)
  const cosCone = Math.cos(TARGETING.cone)
  let cone = -1
  let coneCos = -2
  let coneDist = 0
  let near = -1
  let nearCos = 0
  let nearDist = Infinity
  for (let i = 0; i < ships.length; i++) {
    const t = ships[i]
    if (!isLivingEnemy(shooter, t)) continue
    measure(shooter, t)
    if (measuredCos >= cosCone && (measuredCos > coneCos || (measuredCos === coneCos && measuredDist < coneDist))) {
      cone = t
      coneCos = measuredCos
      coneDist = measuredDist
    }
    if (measuredDist < nearDist) {
      near = t
      nearCos = measuredCos
      nearDist = measuredDist
    }
  }
  if (cone >= 0) return setPick(out, cone, coneCos, coneDist)
  if (near >= 0) return setPick(out, near, nearCos, nearDist)
  return setPick(out, -1, -1, 0)
}

/** Angle ordering: a smaller angle (larger cosine) first, ties by entity id. True when (ca, ea) comes after (cb, eb). */
function after(ca: number, ea: number, cb: number, eb: number): boolean {
  return ca < cb || (ca === cb && ea > eb)
}

/**
 * The next living enemy after the shooter's current `Target` in angle order (off the nose, ascending), wrapping
 * to the one nearest the reticle. Without a valid current target it is `selectTarget`. Pure: does not write `Target`.
 */
export function cycleTarget(ships: ShipList, shooter: number, out: TargetPick): number {
  const current = Target.eid[shooter]
  if (!isLivingEnemy(shooter, current) || !contains(ships, current)) return selectTarget(ships, shooter, out)
  loadNose(shooter)
  measure(shooter, current)
  const curCos = measuredCos
  let next = -1
  let nextCos = 0
  let nextDist = 0
  let first = -1
  let firstCos = 0
  let firstDist = 0
  for (let i = 0; i < ships.length; i++) {
    const t = ships[i]
    if (!isLivingEnemy(shooter, t)) continue
    measure(shooter, t)
    if (first < 0 || after(firstCos, first, measuredCos, t)) {
      first = t
      firstCos = measuredCos
      firstDist = measuredDist
    }
    if (t !== current && after(measuredCos, t, curCos, current) && (next < 0 || after(nextCos, next, measuredCos, t))) {
      next = t
      nextCos = measuredCos
      nextDist = measuredDist
    }
  }
  if (next >= 0) return setPick(out, next, nextCos, nextDist)
  return setPick(out, first, firstCos, firstDist)
}

/**
 * The T / Y action: writes and returns the shooter's new `Target.eid`. With a valid target that sits in the
 * cone (or when no enemy is in the cone) it cycles by angle; otherwise it selects the enemy nearest the reticle,
 * so pointing at a new enemy and pressing T always takes it.
 */
export function targetAction(ships: ShipList, shooter: number, out: TargetPick): number {
  const current = Target.eid[shooter]
  let eid: number
  if (isLivingEnemy(shooter, current) && contains(ships, current)) {
    loadNose(shooter)
    measure(shooter, current)
    const currentInCone = measuredCos >= Math.cos(TARGETING.cone)
    const picked = selectTarget(ships, shooter, out)
    const pickInCone = out.cosAngle >= Math.cos(TARGETING.cone)
    eid = currentInCone || !pickInCone ? cycleTarget(ships, shooter, out) : picked
  } else {
    eid = selectTarget(ships, shooter, out)
  }
  Target.eid[shooter] = eid
  return eid
}

const scratchPick = createTargetPick()

/**
 * Replaces every dead, despawned or no-longer-hostile `Target` with the shooter's next selection (spec §6:
 * destroying the target selects the next one), or -1. Ships with no target (-1) are left alone.
 */
export function validateTargets(ships: ShipList): void {
  for (let i = 0; i < ships.length; i++) {
    const s = ships[i]
    const t = Target.eid[s]
    if (t < 0) continue
    if (isLivingEnemy(s, t) && contains(ships, t)) continue
    Target.eid[s] = selectTarget(ships, s, scratchPick)
  }
}
